import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Card, Statistic, Spin, Empty } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import * as pressReleaseApi from '../../api/pressReleaseApi';
import PressReleaseForm from '../../components/admin/press-release/PressReleaseForm';
import PressReleaseTable from '../../components/admin/press-release/PressReleaseTable';
import Modal from '../../components/common/Modal';

const { Option } = Select;

const PressReleaseManagement = () => {
  // Press Release state
  const [pressReleases, setPressReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPressRelease, setEditingPressRelease] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  // Fetch data
  useEffect(() => {
    fetchPressReleases();
    fetchStats();
  }, []);

  const fetchPressReleases = async () => {
    try {
      setLoading(true);
      const response = await pressReleaseApi.getPressReleases();
      
      console.log('Press Release API response:', response);
      
      // The API returns { pressReleases, totalPages, currentPage }
      if (response.pressReleases && Array.isArray(response.pressReleases)) {
        setPressReleases(response.pressReleases);
      } else {
        setPressReleases([]);
      }
    } catch (error) {
      console.error('Error fetching press releases:', error);
      toast.error('Failed to fetch press releases');
      setPressReleases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await pressReleaseApi.getPressReleaseStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Press Release handlers
  const handleCreate = () => {
    setEditingPressRelease(null);
    setShowForm(true);
  };

  const handleEdit = (pressRelease) => {
    setEditingPressRelease(pressRelease);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this press release?')) return;

    try {
      await pressReleaseApi.deletePressRelease(id);
      toast.success('Press release deleted successfully');
      fetchPressReleases();
      fetchStats();
    } catch (error) {
      console.error('Error deleting press release:', error);
      const errorMsg = error?.message || error?.msg || 'Failed to delete press release';
      toast.error(errorMsg);
    }
  };

  const handleToggle = async (id) => {
    try {
      await pressReleaseApi.togglePressReleaseStatus(id);
      toast.success('Press release status updated');
      fetchPressReleases();
      fetchStats();
    } catch (error) {
      console.error('Error toggling press release status:', error);
      toast.error('Failed to update press release status');
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingPressRelease(null);
    fetchPressReleases();
    fetchStats();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPressRelease(null);
  };

  // Filter press releases
  const filteredPressReleases = Array.isArray(pressReleases)
    ? pressReleases.filter(release => {
        const matchesSearch = release.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             release.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             release.newspaper?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = statusFilter === 'all' || 
                             (statusFilter === 'active' && release.isActive) ||
                             (statusFilter === 'inactive' && !release.isActive);
        return matchesSearch && matchesFilter;
      })
    : [];

  return (
    <>
      <div className="max-w-9xl mx-auto w-full" style={{ fontFamily: 'Rubik, sans-serif' }}>
        <div className="p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              {/* Header can be added here if needed */}
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="large"
              style={{
                backgroundColor: '#470172',
                borderColor: '#470172',
                fontFamily: 'Rubik, sans-serif',
                fontSize: '16px',
                fontWeight: 400
              }}
            >
              Add Press Release
            </Button>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card bordered={false} style={{ 
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Rubik, sans-serif'
            }}>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#000000' }}>Total Press Releases</span>}
                value={stats.total}
                prefix={<FileTextOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '28px', fontWeight: 600, color: '#470172' }}
              />
            </Card>
            <Card bordered={false} style={{ 
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Rubik, sans-serif'
            }}>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#000000' }}>Active</span>}
                value={stats.active}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '28px', fontWeight: 600, color: '#52c41a' }}
              />
            </Card>
            <Card bordered={false} style={{ 
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Rubik, sans-serif'
            }}>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#000000' }}>Inactive</span>}
                value={stats.inactive}
                prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '28px', fontWeight: 600, color: '#ff4d4f' }}
              />
            </Card>
          </div>

          {/* Filters */}
          <Card bordered={false} style={{ 
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '24px',
            fontFamily: 'Rubik, sans-serif'
          }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Search by title, description, or newspaper..."
                prefix={<SearchOutlined style={{ color: '#470172' }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="large"
                style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }}
              />
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                size="large"
                style={{ width: '100%', fontFamily: 'Rubik, sans-serif' }}
              >
                <Option value="all">All Status</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchPressReleases}
                size="large"
                style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }}
              >
                Refresh
              </Button>
            </div>
          </Card>

          {/* Press Releases Table */}
          <Card bordered={false} style={{ 
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            fontFamily: 'Rubik, sans-serif'
          }}>
            {loading ? (
              <div className="text-center py-12">
                <Spin size="large" />
                <p style={{ marginTop: '16px', fontFamily: 'Rubik, sans-serif', color: '#666' }}>
                  Loading press releases...
                </p>
              </div>
            ) : filteredPressReleases.length === 0 ? (
              <Empty 
                description={
                  <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', color: '#666' }}>
                    {pressReleases.length === 0 
                      ? 'No press releases yet. Click "Add Press Release" to create one.' 
                      : 'No press releases match your filters.'}
                  </span>
                }
              />
            ) : (
              <PressReleaseTable
                pressReleases={filteredPressReleases}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleStatus={handleToggle}
              />
            )}
          </Card>
        </div>
      </div>

      {/* Form Modal */}
      <Modal 
        isOpen={showForm} 
        onClose={handleFormCancel} 
        title={editingPressRelease ? 'Edit Press Release' : 'Add Press Release'}
        width="800px"
      >
        <PressReleaseForm
          pressRelease={editingPressRelease}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </Modal>
    </>
  );
};

export default PressReleaseManagement;
